import { useMutation, UseMutationResult } from "react-query";
import type { AxiosResponse } from "axios";
import type { VisitorReceptionTarget } from "../../../features/Admin/components/visitor-reception-api.logic";
import {
  patchConferenceVisitorRegistration,
  type PatchConferenceVisitorRegistrationVariables
} from "./conference-visitor-registration-patch.api";
import {
  patchVisitOnlyBooking,
  type PatchVisitOnlyBookingVariables
} from "./visit-only-patch-booking.api";
import {
  postVisitorReceptionCheckIn,
  postVisitorReceptionCheckOut,
  postVisitorReceptionReject,
  type VisitorReceptionRejectPayload
} from "./visitor-reception-actions.api";
import {
  postVisitOnlyCheckIn,
  postVisitOnlyCheckOut,
  postVisitOnlyReject,
  type VisitOnlyRejectPayload
} from "./visit-only-visit-actions.api";

type VisitOnlyMutationData = AxiosResponse<unknown>;

/** Admin visitor registration: check-in, check-out, reject (cancel), patch. */
export function useRemoteVisitOnlyVisitActions(): {
  checkIn: UseMutationResult<VisitOnlyMutationData, unknown, number>;
  checkOut: UseMutationResult<VisitOnlyMutationData, unknown, number>;
  rejectVisit: UseMutationResult<VisitOnlyMutationData, unknown, VisitOnlyRejectPayload>;
  patchVisitOnly: UseMutationResult<VisitOnlyMutationData, unknown, PatchVisitOnlyBookingVariables>;
  checkInReception: UseMutationResult<VisitOnlyMutationData, unknown, VisitorReceptionTarget>;
  checkOutReception: UseMutationResult<VisitOnlyMutationData, unknown, VisitorReceptionTarget>;
  rejectReception: UseMutationResult<VisitOnlyMutationData, unknown, VisitorReceptionRejectPayload>;
  patchConference: UseMutationResult<
    VisitOnlyMutationData,
    unknown,
    PatchConferenceVisitorRegistrationVariables
  >;
} {
  const checkIn = useMutation<VisitOnlyMutationData, unknown, number>(
    ["visit-only", "check-in"],
    visitId => postVisitOnlyCheckIn(visitId)
  );

  const checkOut = useMutation<VisitOnlyMutationData, unknown, number>(
    ["visit-only", "check-out"],
    visitId => postVisitOnlyCheckOut(visitId)
  );

  const rejectVisit = useMutation<VisitOnlyMutationData, unknown, VisitOnlyRejectPayload>(
    ["visit-only", "reject"],
    ({ visitId, reason }) =>
      postVisitOnlyReject(visitId, reason !== undefined ? { reason } : undefined)
  );

  const patchVisitOnly = useMutation<
    VisitOnlyMutationData,
    unknown,
    PatchVisitOnlyBookingVariables
  >(["visit-only", "patch"], variables => patchVisitOnlyBooking(variables));

  const checkInReception = useMutation<VisitOnlyMutationData, unknown, VisitorReceptionTarget>(
    ["visitor-reception", "check-in"],
    target => postVisitorReceptionCheckIn(target)
  );

  const checkOutReception = useMutation<VisitOnlyMutationData, unknown, VisitorReceptionTarget>(
    ["visitor-reception", "check-out"],
    target => postVisitorReceptionCheckOut(target)
  );

  const rejectReception = useMutation<
    VisitOnlyMutationData,
    unknown,
    VisitorReceptionRejectPayload
  >(["visitor-reception", "reject"], payload => postVisitorReceptionReject(payload));

  const patchConference = useMutation<
    VisitOnlyMutationData,
    unknown,
    PatchConferenceVisitorRegistrationVariables
  >(["conference-visitor-registration", "patch"], variables =>
    patchConferenceVisitorRegistration(variables)
  );

  return {
    checkIn,
    checkOut,
    rejectVisit,
    patchVisitOnly,
    checkInReception,
    checkOutReception,
    rejectReception,
    patchConference
  };
}
